---
title: Thoughts on Brexit
slug: thoughts-on-brexit
date: '2016-06-14'
opener: >-
  Growing up in Northern Ireland I was always taught that when in polite
  company, never discuss religion or politics as it'll likely end in a blazing
  row.
featured: /images/vote-690x450.png
archive: true
---

export const meta = () => [
	{ title: `${frontmatter.title} - Barry McGee` },
	{
		name: 'description',
		content: frontmatter.opener,
	},
	{ property: 'og:title', content: `${frontmatter.title} - Barry McGee` },
	{
		property: 'og:description',
		content: frontmatter.opener,
	},
	{ property: 'og:image', content: 'https://barrymcgee.dev/img/speaking.jpeg' },
	{ property: 'og:type', content: 'article' },
	{
		property: 'og:url',
		content: `https://barrymcgee.dev/notes/${frontmatter.slug}`,
	},
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:site', content: '@barrymcgee' },
	{ name: 'twitter:creator', content: '@barrymcgee' },
]

# {frontmatter.title}

<div className="opener">{frontmatter.opener}</div>

However, as the date of the EU referendum draws ever closer, I’m finding myself
increasingly frustrated with the narrative of public debate and the number of
wild assertions that are seemingly allowed to go unchallenged.

Before going any further, I’ll lay my cards on the table — I’m a solid
Remainian. At this moment however, I fear the Leave campaign will possibly
prevail due to the success of the misinformation campaign they have waged for
the past three months.

How did we get here in the first place? David Cameron thought he could shoot the
UKIP fox by promising an EU referendum and removing the motivation to vote UKIP.
Support for the EU would be surely a shoo-in and that would also finally silence
the raucous Tory backbench.

This not-so-clever strategy has now spectacularly backfired on him as calling
the referendum has only served to galvanise Eurosceptics across all parties and
fired up a public still stinging from the recession and keen to lash out.

Now, what started as an internal Tory party ruck has morphed into the biggest
political choice of my lifetime, the UK sleepwalking towards the precipice and
Downing St left wondering that the hell it was smoking when it promised the
referendum in the first place.

Let's address some of the main themes that have dominated the debate.

## Cost of Membership

Emblazoned on the side of the Leave battle bus is the statement - <em>"We send
the EU £350 million a week - let's fund our NHS instead."</em> This is an
outright lie and despite the Leave campaign being told repeatedly it's a lie, it
remains big and bold on the side of their bus as it travels around the UK.

After accounting for the money that the EU sent back to Britain and EU spending
included in the UK foreign aid target, the net cost was £120 million a week or
£17 million a day in 2015.

£17 million a day is a lot of money when you think what one person could do with
it. However, it's relatively small sum for a developed country of ~65 million
people. The UK spends **less than 0.5%** of overall spending on EU membership.
Per person, that’s 26p a day.

However, the real kicker is that even in the event of Brexit anyway, there would
still be a bill to be paid. If, as the Leave campaign have already inferred, the
UK would request access to the EU market in much the same way as Norway, the UK
would have to pay for it. The net payment Norway makes for its arrangement is
**about the same** as the UK pays currently per person - except of course, they
have no say at the EU table.

UK annual spend is a mind-boggling 700 billion - don't allow the Leave campaign
to over-awe you with big numbers. Sometimes, even big numbers can be smaller
than you think.

## Immigration

The biggie. I’ve always thought that no matter what anyone else said, the issue
of immigration would decide this referendum. Decrying immigration has been
UKIP's _raison d'être_ from day one and UKIP are the number one reason this
referendum was called in the first place.

I agree that there is a big problem in the UK with immigration, or at the very
least, there's a big problem with the _perception_ of immigration. The common
complaint is that immigrants are coming over here, squeezing public services,
taking all the houses, taking all the jobs, or if they're not taking all the
jobs - they're claiming all the benefits.

Official figures have repeatedly confirmed that these perceptions are not true.
Immigrants are net contributors to the UK \[1]. 91% of social housing goes to UK
nationals \[2]. 82% of non-UK nationals never use social housing in their lives.
\[2] Less than 3% of UK benefits is given to immigrants. \[3]

In truth, the squeeze in public services is not because of immigrants but
under-funding and mismanagement by successive governments particularly
exacerbated in recent years by acute austerity.

This perception and rage is compounded by a hysterical right wing media. The Sun
& The Daily Mail are the two most popular daily newspapers in the UK, with a
combined daily circulation of about 5 million.

Their typical playbook involves screaming an outrage on the front page about the
EU / Migrants / straight bananas before quietly admitting it was all horse shit
in the corner of page 20 a couple of days later.

The problem is that people don't want to hear reasoned truth and logic because
at the root of their discontent is an emotional reaction to what they see as a
loss of their culture and way of life.

People don't care about dry facts and figures about GDP etc when what used to be
their local pub is now a Polish deli & the local primary school they went to now
has 25 distinct languages reverberating around the playground.

<blockquote class="twitter-tweet" data-lang="en">
	<p lang="en" dir="ltr">
		Whaddya know - I was bang on! ⚡️ &quot;Did the Daily Mail mislead with this
		migrant headline?&quot;
		<a href="https://t.co/lwXgpwOIeI">https://t.co/lwXgpwOIeI</a> <a href="https://t.co/H94e4Vqlmu">https://t.co/H94e4Vqlmu</a>
	</p>
	&mdash; Barry McGee (@barrymcgee) <a href="https://twitter.com/barrymcgee/status/743445678521720832">June 16, 2016</a>
</blockquote>
<script async src="//platform.twitter.com/widgets.js" charset="utf-8"></script>

During the 2010 General Election, then Prime Minister Gordon Brown crossed paths
with local woman Gillian Duffy on the campaign trail in Rochdale.

In nine schools across Rochdale, 70 per cent of the children speak English as a
foreign language [4]. In a now
[infamous exchange](http://www.theguardian.com/politics/2010/apr/28/gordon-brown-gillian-duffy-transcript),
she dared complain about the effects of immigration on the local community and
he called her a bigot once he (thought he) was safely back in his ministerial
car.

Failure of government to recognise this effect of EU freedom of movement on
working-class communities and address it without insinuating that anyone
complaining is a racist or bigoted has created a festering rage that we now see
boiling over.

This fear and perception is not just confined to places where immigration is
high. It's arguably more acutely felt in places where immigrants are rare but
anecdotes are plentiful. I suppose it's much easier to advocate kicking out all
the immigrants when you don't count any of them as friends or family.

<blockquote>
<p>"My brother and sister have lived all their life in Devon and rarely encounter anyone who is foreign or even black. Consequently they are very pro-Leave."</p>
<cite>Mary Bonney - <a href="http://infacts.org/uks-regions-brexiting-foot/#comment-15570">InFacts</a></cite>

</blockquote>

However, the Leave campaign are insinuating Brexit will be the silver bullet to
solve all immigration woes. It will not. Only about 50% of net migration last
year was from within the EU. The other 50%, the UK Border Agency already has
full control over and the figures have still not reduced. This is because the UK
economy needs migrants to prosper.

Put simply - leaving the EU will not fix the issues people currently have with
immigration.

Large scale immigration in the last two decades hasn't been entirely due to EU
membership either. The internet has made the world a hell of a lot smaller not
to mention cheap air travel. The natural out-workings of these changes is a more
diverse mix in developed countries where opportunities are plentiful.

This is simply a fact of life and has been happening since long before the UK
joined the EU - people who have limited opportunities where they are will
gravitate to places where there are more opportunities, why do you think America
is full of Irish?

Many older voters, which polls are suggesting are much more likely to vote
Leave, have spoken of their desire to return to how things were. This will not
happen - the world has moved on. Britain post-Brexit will not return to a 1950's
postcard of Blackpool.

The UK should remain in the EU to influence and shape these migration challenges
rather than attempt to pull up the drawbridge.

## Sovereignty

Capital been made with jingoistic Leave rhetoric of "Let's make Britain Great
again" and "Let's take back control". Joe Public is now parroting back these
lines in full throated rebukes on the evening news - but, what do they actually
mean?

<p class="post__note">
	<em>Note:</em> I wrote this draft at the start of this week before the heinous
	murder of Jo Cox by a delusional psychopath shouting "Britain first". Now,
	re-reading the paragraph above, it's hard not feel angry about the
	irresponsibility of cultivating a climate where someone thought it was
	entirely justified to carry out such an act. Alex Massie wrote a very eloquent
	response to the attack in The Spectator - "
	<a href="http://blogs.spectator.co.uk/2016/06/a-day-of-infamy/">
		Sometimes, rhetoric has consequences.
	</a>
	"
</p>

After 40 years of unfettered access to the world's largest trading bloc, Britain
is currently sitting as the fifth largest economy in the world. Crime is down.
Unemployment is down. Household income up. Healthy life expectancy has soared.
Air quality has vastly improved. Access to green and public spaces such as
forestry has hugely increased.

When exactly did Britain stop being Great? Where and what did it lose control
of? And more importantly, how exactly will leaving the EU make Britain greater?

The Leave campaign is big on rhetoric but small on actual detail.

Taking back control is the other big call. Leave continually bang an angry drum
about unelected EU bureaucrats but fail to acknowledge that the UK electorate
(or any electorate for that matter) never really calls all the shots.

Sovereignty is not a baton to be passed back to the people on June 24th - it's
inherently multi-faceted and is diluted at every level in some way.

For example, The Secretary of State for Defense is a politician, not an Army
General, and as such needs to rely on a huge team of unelected senior civil
servants to make crucial decisions. This is true across all government
departments. Politicians don't make policy, senior civil servants make policy.

Only 24% of eligible voters in the UK voted Conservative in the last election
and the Tories now wield majority rule. On top of that, they appoint (not elect)
the House of Lords which currently boasts over 700 members. Oh, and who sits on
top of that pile? An hereditary monarch.

Don't be fooled by the illusion of complete sovereignty.

## Economy

This is where the wheels really comes off the Leave cart. There is not a single
reputable financial organisation that predicts that Brexit would be good for the
UK economy.

The IMF (International Monetary Fund) has said Brexit could "**entail sharp
drops in equity and house prices**, increased borrowing costs for households and
businesses... \[5]"

The OECD (Organisation for Economic Co-operation and Development) has said
Brexit “would be a major negative shock to the UK economy... In some respects,
Brexit would be akin to a tax on GDP, imposing a **persistent and rising cost on
the economy** that would not be incurred if the UK remained in the EU. \[6]”

The WTO (World Trade Organisation) head says leaving EU would **cost UK
consumers £9bn a year** [7].

The UK Treasury long-term economic analysis of a British exit from the EU
arrives at a central estimate that it would "ultimately **lower UK economic
output 6.2 per cent**, amounting to an annual cost to British households of
£4,300 a year" \[8].

The CBI (Confederation of British Industry) says Brexit could "cost UK economy
£100 billion and **cost 950,000 jobs by 2020**".

I could go on.. there is no end of financial experts who predict, on a balance
of probabilities, that Brexit will be detrimental.

<blockquote class="twitter-tweet" data-lang="en">
	<p lang="en" dir="ltr">
		12 Nobel Prize winners and 150 economists warn Brexit &#39;likely lead to
		recession, job losses,
		<br />
		inflation, higher taxes &amp; cuts to services&quot; 1/2
	</p>
	&mdash; Jason Beattie (@JBeattieMirror) <a href="https://twitter.com/JBeattieMirror/status/744911322383527937">June 20, 2016</a>
</blockquote>
<script async src="//platform.twitter.com/widgets.js" charset="utf-8"></script>

The most common response from Leave I've heard to these stats is that they're
"scare-mongering", however Leave has not been able to rebuke them with any
robust figures.

Nigel Farage has even admitted that a hit is inevitable but it'll be a "price
worth paying" for fewer immigrants [9].

Despite his man of the people schtick Farage is a
[wealthy man](http://www.londonlovesbusiness.com/business-news/politics/how-rich-is-nigel-farage/9363.article).
I wonder how many working class people will still think it's a price worth
paying when they're the guts of five grand worse off come end of next year? As a
wise man in Belfast once said, you can't eat a flag.

When it comes to Northern Ireland, the prospects are even more stark. As a small
economy, sharing a land border with the EU post-Brexit - NI's position is even
more vulnerable. This has been echoed by many reputable voices over the recent
months.

Earlier this week the ex-head of the World Trade Organisation described Brexit
an <em>"act of wanton destruction"</em> for the Northern Irish economy

<small>\[10]</small>. Dead hand on the NI wheel and prominent Leave campaigner
Theresa Villiers dismissed this view as from a "so-called expert" - if the
ex-Head of the World Trade Organisation is not an expert on global trade, who
is?

<blockquote>
	<p>
		"Those who invest in Ireland, north or south, are doing so because it
		provides them with the manufacturing base to sell to the European Union"
	</p>
	<cite>
		Peter Sutherland -{' '}
		<a href="http://www.bbc.co.uk/news/uk-northern-ireland-36510787">
			BBC News
		</a>
	</cite>
</blockquote>

A further fall in unemployment figures this week has resulted in NI having the
record
[levels of employment](http://www.bbc.co.uk/news/uk-northern-ireland-36536332).

Why would we put that at risk?

Why ignore experienced voices?

Why dismiss people who have spent their professional lives studying this stuff?

## War & Peace

The EU was originally set up after World War Two. While on the face of it, it
started simply a coal and steel trade agreement between France and Germany the
architect, Charles De Gaulle, had an ulterior motive - to make it simply
unthinkable that any of the great nations of Europe could go to war with each
other again due to their common interdependence.

While it's ridiculous to suggest that World War Three will break out anytime
soon, though Boris found time to rubbish the claim that no-one claimed anyway,
since the EU was formed we have enjoyed the longest period of peace in written
history, over 2000 years.\[11]

When we're living in a time where there is a surge from the far-right
nationalism in France, Poland, Austria and Germany - surely it's too not too
much of a stretch to point out that European unity can only reduce the risk of
conflict in the future and countries breaking apart from the EU might increase
the risk?

On the international stage, every leader of note from the US to Australia to
China and back to Canada thinks the prospect of Brexit is bonkers. There are
three notable exceptions though - Marine Le Pen, Vladimir Putin & Donald J.
Trump. I'll leave that with you.

Looking to Northern Ireland - again this is where NI is more vulnerable than the
rest of the UK. One of the major tenets underpinning the Good Friday Agreement
was that the UK & Ireland shared common membership of the EU which allowed the
border to become invisible to the untrained eye.

Theresa Villiers will tell you that Ireland the UK shared a common travel area
long before the EU was formed and will do so again. What'll she'll fail to add
is that Ireland and the UK joined the EU on the same day and therefore the Irish
border has never been a land border of the EU - a crucial difference.

I grew up 3 miles from the Irish border and remember when it was fortified
vividly. We sat queued in the back of the car at border custom checkpoints en
route to buy bread, milk and lotto tickets (before the UK Lotto was a thing).

Our local pub is now the other side of that border. It now separates my parents
from my sister but you'd never know to drive it. From Dundalk to Derry - 30,000
people criss-cross the border daily for work and leisure without thinking.
Border dwellers are
[dreading Brexit](http://www.theguardian.com/politics/2016/may/23/beyond-borders-the-irish-villages-dreading-a-brexit-vote).
It's unthinkable that we may be voting for the days of old to return.

The Leave campaign is currently asserting that they'll take back control of UK
borders and that there'll be no changes to the Irish border. They can't be right
on both counts. In the case of Brexit increased border controls are an
inevitability - it's not a case of If, but Where?

The peace process has been lurching along in fits and starts for the best part
of two decades and is lauded internationally as a genuine success story.
However, lets not forget it's only four years ago that changing the flag
protocol at Belfast City Hall to reflect that of Buckingham Palace caused months
of civil disobedience and street protests that continue weekly to this day. In
Northern Ireland, symbolism trumps pragmatism. What happens when fences start to
appear along the border again?

<blockquote>
	<p>
		"The fear in Dublin is that our border towns would become a backdoor into
		the UK. In that instance what sort of fortress would the Northern Ireland
		border have to become to close that backdoor?"
	</p>
	<cite>
		Phil Hogan, European Commissioner for Agriculture, 9 May 2016 -{' '}
		<a href="https://fullfact.org/europe/eu-referendum-and-irish-border/">
			FullFact.org
		</a>
	</cite>
</blockquote>

On the positive side, the EU has made a huge contribution to peace and community
relations in Northern Ireland. The Peace Bridge in Derry connecting the Fountain
to the Bogside was EU funded and the EU awards grants to many community groups
to promote cross-community collaboration.

The EU funded the renovation of the Waterfront Hall in Belfast. The EU funded
the Giants Causeway Visitor Centre. The EU funded the recent refurbishment of
the Enterprise cross-border train. The EU funds the Skainos centre hosting Irish
classes in East Belfast.

The EU also funds many other smaller projects I knew nothing about until I
researched EU funding for this post. Relatively obscure projects such as
upgrading the quay walls in Kilkeel and repositioning Dungannon's civic spaces.
Funding towards a cold store and blast freezing facility for Ardglass Sea
Products and funding to help restock the River Bann with eel.

These may seem insignificant in the big picture but they're hugely significant
to the people they benefit and these EU beneficiaries are scattered across the
country. In a climate where the UK Government can't keep public libraries open -
how much of this funding do we think will be replaced post-Brexit?

I haven't even touched on the Single Farm Payment, without which NI farmers
would have made **zero profit** last year.

Brexitiers argue that there'll be pots of money to go round for this sort of
thing when we're no longer paying towards the EU.

But the question is, where will Prime Minister Boris share those pots?

Do you trust him to match or better what the EU currently sends to Northern
Ireland every year? I know I don't.

## One choice

This is not a general election - a vote to Leave cannot be reversed in five
years.

If you're undecided, err on the side of caution. Use your vote wisely - **vote
Remain** on June 23rd.

### References

1. [Mass EU migration into Britain is actually good news for UK economy](https://www.theguardian.com/uk-news/2016/feb/18/mass-eu-migration-into-britain-is-actually-good-news-for-uk-economy)
2. [EEA migrants: access to social housing (England)](http://researchbriefings.parliament.uk/ResearchBriefing/Summary/SN04737)
3. [EU migrants on benefits: separating the statistics from the spin](https://www.theguardian.com/uk-news/datablog/2015/nov/10/eu-migrants-on-benefits-separating-the-statistics-from-the-spin)
4. [Rochdale: One town's story of immigration](http://www.telegraph.co.uk/news/election-2010/7663660/Rochdale-One-towns-story-of-immigration.html)
5. [Brexit would prompt stock market and house price crash, says IMF](https://www.theguardian.com/business/2016/may/13/imf-warns-stock-market-crash-house-price-fall-eu-referendum-brexit)
6. [The Economic Consequences of Brexit: A Taxing Decision](http://www.oecd.org/economy/the-economic-consequences-of-brexit-a-taxing-decision.htm)
7. [WTO head says leaving EU would cost UK consumers £9bn a year](https://www.theguardian.com/business/2016/may/25/wto-eu-uk-consumers-trade-deals)
8. [Treasury’s Brexit analysis: what it says — and what it doesn’t](https://next.ft.com/content/c15cd060-0550-11e6-96e5-f85cb08b0730)
9. [I'd rather be poorer with fewer migrants, Farage says](http://www.telegraph.co.uk/news/uknews/immigration/10555158/Id-rather-be-poorer-with-fewer-migrants-Farage-says.html)
10. [Brexit an 'act of wanton destruction' for NI economy](http://www.bbc.co.uk/news/uk-northern-ireland-36510787)
11. [How did Europe construct its longest peacetime ever?](http://www.brusselstimes.com/opinion/2974/how-did-the-europe-construct-its-longest-peacetime-ever)
